import { useCallback, useMemo, useState } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import type { IUserData } from '../../../shared/types';
import { AppLink } from '../../components/AppLink';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { ScrollShadow } from '../../components/ScrollShadow';
import { auth, provider, signInWithPopup } from '../../features/auth/lib/firebase';
import { sendMessageToNative } from '../../features/nativeBridge';
import { getDebugRing } from '../../lib/devUtils';
import { setUserData } from '../../lib/userData/UserDataProvider';
import { clx } from '../../lib/utils';

export const LoginForm = ({ className }: { className?: string }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleGoogleLoginWeb = useCallback(async () => {
    const userCredential = await signInWithPopup(auth, provider).catch((error) => {
      console.error('Error during login:', error);
    });

    console.log(`%cthis is the userCredential`, 'background:#bada55; color:#222;', userCredential);

    console.log(
      `%ctrigger an update to the userData provider (handleGoogleLogin)`,
      'background:pink; color:black;'
    );

    // navigate('/home');
  }, []);

  const handleGoogleLogin = useCallback(async () => {
    // This is for accessing the web app from the native shell.
    if (window.ReactNativeWebView) {
      sendMessageToNative('LOGIN_REQUESTED');
    } else {
      handleGoogleLoginWeb();
    }
  }, []);

  const passwordsMatch = useMemo(() => {
    return isSignUpMode ? password === confirmPassword : true;
  }, [isSignUpMode, password, confirmPassword]);

  const isSubmitDisabled = useMemo(() => {
    return isLoading || email.trim().length === 0 || password.trim().length < 6 || !passwordsMatch;
  }, [email, password, isLoading, passwordsMatch]);

  const handleEmailContinue = useCallback(async () => {
    setErrorMessage(null);
    setIsLoading(true);
    try {
      if (isSignUpMode && !passwordsMatch) {
        setErrorMessage('Passwords do not match.');
        return;
      }
      const firebaseAction = isSignUpMode
        ? createUserWithEmailAndPassword
        : signInWithEmailAndPassword;
      const result = await firebaseAction(auth, email.trim(), password);
      console.log('result', result);

      const response = await fetch('/api/token', {
        method: 'POST',
        body: JSON.stringify({ token: await result.user.getIdToken() }),
      });
      console.log('response', response);

      const data = await response.json();

      setUserData(data.userData as IUserData);
    } catch (error) {
      // Normalized error message for display purposes.
      const message = error instanceof Error ? error.message : 'Authentication failed.';
      setErrorMessage(message);
      console.error('Email/password auth error.', error);
    } finally {
      setIsLoading(false);
    }
  }, [email, password, isSignUpMode, passwordsMatch]);

  return (
    <ScrollShadow
      outerClassName={className}
      innerClassName={clx(
        'flex w-full flex-col items-center justify-start',
        getDebugRing('purple')
      )}
    >
      {/* Email and Password Section */}
      <div
        className={clx(
          'flex min-h-36 w-full shrink basis-44 flex-col justify-around text-center',
          isSignUpMode && 'min-h-40',
          getDebugRing('green')
        )}
      >
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
        />
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
        />
        {isSignUpMode && (
          <Input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm password"
          />
        )}

        {/* Forgot Password Link. */}
        {!isSignUpMode && (
          <Button as={AppLink} href="/forgot-password" className="mx-auto text-center">
            Forgot password?
          </Button>
        )}
      </div>

      {/* Login Button */}
      <div
        className={clx(
          'flex min-h-20 w-full shrink basis-28 flex-col items-center justify-center',
          getDebugRing('orange')
        )}
      >
        {!!errorMessage && <p className="mb-2 text-sm text-red-600">{errorMessage}</p>}
        <Button
          variant="buttonPrimary"
          onClick={handleEmailContinue}
          disabled={isSubmitDisabled}
          className="bg-cta-primary w-full text-[22px] font-semibold disabled:opacity-60"
        >
          {isSignUpMode
            ? isLoading
              ? 'Creating account…'
              : 'Create account'
            : isLoading
              ? 'Logging in…'
              : 'Login'}
        </Button>
      </div>

      {/* Divider */}
      <div className={clx('flex w-full basis-5 items-center space-x-4', getDebugRing('purple'))}>
        <div className="flex-1 border-t border-black"></div>
        <span className="font-medium">or</span>
        <div className="flex-1 border-t border-black"></div>
      </div>

      <div
        className={clx(
          'mt-1 flex min-h-16 w-full shrink basis-32 flex-col items-center justify-center',
          getDebugRing('blue')
        )}
      >
        {/* Google Login */}
        <Button
          variant="buttonPrimary"
          onClick={handleGoogleLogin}
          className={clx('flex w-full items-center justify-center gap-3 rounded-2xl font-medium')}
        >
          <img src="/google-icon.svg" alt="Google" width={25} height={25} />
          Continue with Google
        </Button>
      </div>

      {/* Login Link */}
      <div className="text-center">
        <span>
          {isSignUpMode ? (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  setIsSignUpMode(false);
                  setErrorMessage(null);
                  setConfirmPassword('');
                }}
                className="underline"
              >
                <strong>Log in</strong>
              </button>{' '}
              instead.
            </>
          ) : (
            <>
              Don&apos;t have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  setIsSignUpMode(true);
                  setErrorMessage(null);
                }}
                className="underline"
              >
                <strong>Sign up</strong>
              </button>{' '}
              for free.
            </>
          )}
        </span>
      </div>
    </ScrollShadow>
  );
};
